import { useEffect, useState } from "react";
import { portalApi, INR, Page, StatCard } from "./PortalShell";
import { Wallet, TrendingUp, ChevronDown, ChevronRight, Sparkles, Gift } from "lucide-react";

const MONTH_LABEL = (mk) => {
  const [y, m] = mk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const STATUS_STYLE = {
  paid: "text-emerald-400 bg-emerald-500/10",
  pending: "text-amber-400 bg-amber-500/10",
  preview: "text-sky-400 bg-sky-500/10",
};

const STATUS_LABEL = {
  paid: "Paid",
  pending: "Pending",
  preview: "Live preview",
};

// Playful comparison for lifetime earnings — grows quirkier as the number climbs.
function quirkyComparison(amount) {
  const n = Number(amount) || 0;
  if (n <= 0) return "Your first pay-out is one sale away — let's get you on this list.";
  if (n < 5_000)   return `That's already a fancy dinner for two on Fuelled 🍝`;
  if (n < 15_000)  return `Enough for a solid weekend getaway — thanks for showing up 🏖️`;
  if (n < 50_000)  return `That's a new phone paid for by your community. Ridiculous.`;
  if (n < 100_000) return `A round-trip international flight — booked. On us, kind of.`;
  if (n < 250_000) return `A brand-new 2-wheeler. Literally rode here on your posts.`;
  if (n < 500_000) return `A full year of rent in most Indian cities. Casually.`;
  if (n < 1_000_000) return `That's a proper Europe trip. Business class if you're careful 😏`;
  if (n < 2_500_000) return `You could buy a car outright. Cash. From Fuelled money.`;
  return `You've built a small business out of thin air. Absolutely unreal.`;
}

export default function PortalPayoutsPage() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});   // { [month]: { loading, orders } }
  const [err, setErr] = useState("");

  useEffect(() => {
    portalApi.get("/affiliate/payouts").then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);

  const toggle = (mk) => {
    if (expanded[mk]?.orders) {
      setExpanded((prev) => ({ ...prev, [mk]: { ...prev[mk], open: !prev[mk].open } }));
      return;
    }
    setExpanded((prev) => ({ ...prev, [mk]: { open: true, loading: true } }));
    portalApi.get(`/affiliate/payouts/${mk}/orders`)
      .then((r) => setExpanded((prev) => ({ ...prev, [mk]: { open: true, loading: false, orders: r.data.orders } })))
      .catch(() => setExpanded((prev) => ({ ...prev, [mk]: { open: true, loading: false, orders: [] } })));
  };

  if (err) return <Page title="Payouts"><div className="text-red-400">{err}</div></Page>;
  if (!data) return <Page title="Payouts"><div className="text-neutral-500 italic">Loading…</div></Page>;

  // Monthly list still hides pre-Aug 2026 rows (internal reconciliation only).
  const visibleMonths = (data.months || []).filter((m) => m.month >= "2026-08");
  // Lifetime KPIs use the FULL history (`data.lifetime_*`) so affiliates see everything
  // they've earned across their entire association with Fuelled.
  const lifetimeCommission = data.lifetime_commission || 0;
  const lifetimeSales = data.lifetime_sales || 0;
  const lifetimeFreeProducts = data.lifetime_free_products_mrp || 0;
  const totalMonthsActive = (data.months || []).filter((m) => (m.commission_amount || 0) > 0).length;
  const congratsLine = quirkyComparison(lifetimeCommission);
  const totalLifetimeValue = lifetimeCommission + lifetimeFreeProducts;

  return (
    <Page title="Payouts" subtitle="Your monthly earnings & payment status" testId="portal-payouts-page">
      {/* Two separate hero blocks — Commission (cash) + Free Products (stack) */}
      {totalLifetimeValue > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Block 1 — Lifetime Commission Earned (orange) */}
          <div
            data-testid="lifetime-commission-hero"
            className="rounded-3xl bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 p-5 lg:p-6 relative overflow-hidden"
          >
            <Sparkles className="absolute top-4 right-4 h-4 w-4 text-white/30 animate-pulse" />
            <div className="text-[11px] uppercase tracking-widest text-white/80 flex items-center gap-1.5">
              <Wallet className="h-3 w-3" /> Lifetime Commission Earned
            </div>
            <div className="mt-2 text-4xl sm:text-5xl font-black tracking-tight tabular-nums leading-none">
              ₹{Math.round(lifetimeCommission).toLocaleString("en-IN")}
            </div>
            <div className="mt-3 text-[11px] text-white/85">
              Cash paid out across <b className="text-white">{totalMonthsActive}</b> active month{totalMonthsActive === 1 ? "" : "s"} · <b className="text-white">₹{Math.round(lifetimeSales).toLocaleString("en-IN")}</b> in sales driven.
            </div>
            <div className="mt-2 text-[11px] text-white/80 italic">{congratsLine}</div>
          </div>

          {/* Block 2 — Lifetime Free Product Earnings (emerald) */}
          <div
            data-testid="lifetime-free-products-hero"
            className="rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 p-5 lg:p-6 relative overflow-hidden"
          >
            <Gift className="absolute top-4 right-4 h-4 w-4 text-white/30" />
            <div className="text-[11px] uppercase tracking-widest text-white/80 flex items-center gap-1.5">
              <Gift className="h-3 w-3" /> Lifetime Free Product Earnings
            </div>
            <div className="mt-2 text-4xl sm:text-5xl font-black tracking-tight tabular-nums leading-none">
              ₹{Math.round(lifetimeFreeProducts).toLocaleString("en-IN")}
            </div>
            <div className="mt-3 text-[11px] text-white/85">
              MRP of Fuelled stack shipped free to you as performance & milestone rewards.
            </div>
            {lifetimeFreeProducts > 0 && (
              <div className="mt-2 text-[11px] text-white/80 italic">
                That's a stack most coaches pay for — you earned it by helping clients win.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Secondary KPI row — sales driven + months active only (Commission + Free Products
          are already prominent in the two hero blocks above). */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={TrendingUp} label="Lifetime Sales Driven" value={INR(lifetimeSales)} testId="payout-lifetime-sales" />
        <StatCard label="Months Active" value={totalMonthsActive} testId="payout-months-active" />
      </div>

      {/* Milestone rewards (Fast Track / Tier unlocks / Referrals) */}
      {(data.milestone_rewards || []).length > 0 && (
        <div data-testid="section-milestone-rewards">
          <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Milestone Rewards</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.milestone_rewards.map((r, i) => (
              <div
                key={`${r.type}-${i}`}
                data-testid={`milestone-reward-${r.type}`}
                className="rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3 flex items-center gap-3"
              >
                <div className="h-9 w-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <Gift className="h-4 w-4 text-emerald-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.label}</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">Free Fuelled stack</div>
                </div>
                <div className="text-sm font-semibold text-emerald-300 tabular-nums">{INR(r.mrp)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly list */}
      <div data-testid="section-monthly-payouts">
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Monthly Breakdown</div>
        {visibleMonths.length === 0 ? (
          <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-8 text-center text-neutral-500 text-sm">No payout history yet.</div>
        ) : (
          <div className="space-y-2">
            {visibleMonths.map((m) => {
              const st = expanded[m.month];
              const isOpen = st?.open;
              const statusLabel = STATUS_LABEL[m.status] || (m.status.charAt(0).toUpperCase() + m.status.slice(1));
              return (
                <div key={m.month} data-testid={`payout-row-${m.month}`} className="rounded-2xl bg-neutral-900 border border-neutral-800 overflow-hidden">
                  <button onClick={() => toggle(m.month)} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-neutral-900/60 transition-colors">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-neutral-500 shrink-0" /> : <ChevronRight className="h-4 w-4 text-neutral-500 shrink-0" />}
                      <div className="text-left min-w-0">
                        <div className="text-sm font-semibold">{MONTH_LABEL(m.month)}</div>
                        <div className="text-[10px] text-neutral-500 mt-0.5">
                          {m.new_customers} new · {INR(m.sales_total)} sales · {m.commission_percent}%
                          {m.free_products_mrp > 0 && (
                            <span className="ml-1 text-emerald-400"> · +{INR(m.free_products_mrp)} free stack</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_STYLE[m.status] || STATUS_STYLE.pending}`}>{statusLabel}</div>
                      <div className="text-base font-semibold text-orange-400">{INR(m.commission_amount)}</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-neutral-800 bg-neutral-950/60 px-4 py-3">
                      {st.loading ? (
                        <div className="text-xs text-neutral-500 italic py-3">Loading orders…</div>
                      ) : st.orders?.length === 0 ? (
                        <div className="text-xs text-neutral-500 italic py-3">No orders for this month.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-[9px] uppercase tracking-widest text-neutral-500">
                                <th className="pb-2">Order</th>
                                <th className="pb-2 hidden sm:table-cell">Customer</th>
                                <th className="pb-2 text-right">Amount</th>
                                <th className="pb-2 text-right">Commission</th>
                                <th className="pb-2 text-right">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {st.orders.map((o, i) => (
                                <tr key={i} className="border-t border-neutral-800/60">
                                  <td className="py-2 font-mono">
                                    {o.shopify_number || "—"}
                                    {o.is_new_customer && <span className="ml-1 text-[9px] font-semibold text-orange-400 uppercase">New</span>}
                                  </td>
                                  <td className="py-2 hidden sm:table-cell truncate max-w-[180px]">{o.customer_name || o.customer_email || "—"}</td>
                                  <td className="py-2 text-right font-semibold">{INR(o.amount)}</td>
                                  <td className="py-2 text-right text-emerald-400">{INR(o.commission_amount)}</td>
                                  <td className="py-2 text-right text-neutral-500">{o.order_date?.slice(0, 10)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Page>
  );
}
